/**
 * How much — in lots, or in rupees — ROADMAP 4.17.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO CONTROLS, ONE AT A TIME
 *
 * A desk sizes either by LOTS ("2 lots of NIFTY") or by EXPOSURE ("₹50 L of
 * premium"). Both are shown in the same slot because two live size inputs
 * side by side are two answers to "how much", and only one of them sends.
 *
 * ₹ IS PREMIUM, NOT MARGIN, and the hint says so. The budget is divided by
 * the premium of the contract being clicked — what a bought option costs,
 * and what a sold one collects. It is NOT a cap on the capital a short
 * position puts at risk: [B9] leaves this gateway with no margin endpoint,
 * so there is no margin figure to divide by and none is implied here.
 *
 * ARMING IS A SEPARATE STEP. A click fires immediately (4.15), so a
 * half-typed ₹5,00,000 that is momentarily ₹5 must never be able to size an
 * order. The amount is inert until OK, and editing it disarms.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { Check } from 'lucide-react'
import { DigitInput } from '@/components/DigitInput'
import { autoLotsFor, BUDGET_PRESETS, compactInr } from './quantity'

/**
 * The most this box will let you type. NOT a risk limit.
 *
 * It is a paste guard: without a ceiling, `Number('999999999999999999')`
 * reaches the sizing maths as something no downstream check expects.
 *
 * THE ORDER PATH IS STRICTER. `INTENT_LIMITS.maxLots` is 10,000 and
 * `orderIntent.service.ts` refuses any leg above it, so a size between that
 * and this ceiling builds an order the server will refuse by name. That is a
 * deliberate choice of the owner's — the field should not fight you while
 * typing — and the refusal is readable when it happens.
 */
const MAX_LOTS_TYPED = 10_000_000

/** The same guard for the rupee budget, at the width it used to truncate to. */
const MAX_BUDGET_TYPED = 999_999_999_999

export function LotsSize({
  lots,
  lotSize,
  onLots,
}: {
  lots: number
  lotSize: number
  onLots: (lots: number) => void
}) {
  return (
    <>
      <div className="tr-stepper">
        <button type="button" aria-label="Decrease size" onClick={() => onLots(Math.max(1, lots - 1))}>
          −
        </button>
        <DigitInput
          value={lots}
          onValue={onLots}
          max={MAX_LOTS_TYPED}
          /*
           * `null`, so an empty box leaves the parent on its last real size.
           * Zero lots is not an order, and writing 0 through here would make
           * every B and S refuse until something was retyped.
           */
          emptyValue={null}
          label="Order size in lots"
          testId="qty-lots"
          placeholder="1"
          className="val num w-[54px] border-0 bg-transparent text-center outline-none"
        />
        <button type="button" aria-label="Increase size" onClick={() => onLots(lots + 1)}>
          +
        </button>
      </div>
      {/*
        The units an order will actually carry. "1 × 65 = 65" showed the
        working as well as the answer; the lot count is already in the
        stepper beside it, so only the answer is new.
      */}
      <span className="tr-hdr-hint num text-[11px] text-muted-foreground">
        {lotSize ? `${lots * lotSize} qty` : `${lots} lot${lots === 1 ? '' : 's'}`}
      </span>
    </>
  )
}

export function RupeeSize({
  budget,
  armed,
  lotSize,
  atmPremium,
  onBudget,
  onArm,
}: {
  budget: number
  armed: boolean
  lotSize: number
  /** The ATM call's premium, for the "what would this buy" hint. 0 if unknown. */
  atmPremium: number
  onBudget: (value: number) => void
  onArm: () => void
}) {
  // The hint prices the budget against the ATM, which is the strike a reader
  // can check it against. Every other strike is cheaper or dearer, and the
  // real number is computed per click from that contract's own premium.
  const atm = autoLotsFor({ budget, premiumPerUnit: atmPremium, lotSize })

  return (
    <>
      <div className={`tr-budget${armed ? ' armed' : ''}`}>
        <span className="cur">₹</span>
        <DigitInput
          value={budget}
          onValue={onBudget}
          max={MAX_BUDGET_TYPED}
          // Zero IS the budget's empty: no budget means nothing is armed.
          emptyValue={0}
          label="Budget in rupees"
          testId="qty-budget"
          placeholder="0"
          className="val num"
        />
        <span className="compact num">{compactInr(budget)}</span>
      </div>

      {BUDGET_PRESETS.map((p) => (
        <button
          key={p.label}
          type="button"
          data-testid={`budget-${p.label}`}
          onClick={() => onBudget(p.value)}
          className={`tr-chip tr-budget-extra num${budget === p.value ? ' on' : ''}`}
        >
          {p.label}
        </button>
      ))}

      <button
        type="button"
        data-testid="budget-arm"
        onClick={onArm}
        aria-pressed={armed}
        className={`tr-arm${armed ? ' on' : ''}`}
      >
        {armed ? (
          <>
            <Check size={12} /> ARMED
          </>
        ) : (
          'OK'
        )}
      </button>

      <span
        className="tr-hdr-hint tr-budget-extra text-[11px] text-muted-foreground"
        data-testid="budget-hint"
      >
        {armed ? (
          <>
            click any strike — qty from its own premium
            {atm.ok && <span className="num"> · ≈{atm.lots} lots at ATM</span>}
          </>
        ) : (
          <>set amount → OK{atm.ok && <span className="num"> · ≈{atm.lots} lots at ATM</span>}</>
        )}
      </span>
    </>
  )
}

/**
 * The rupee amount, typed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT HOLDS A STRING AND NOT THE NUMBER
 *
 * It used to be `value={budget}` over a number. React only touches the DOM
 * when the value PROP changes, and several different strings parse to the
 * same number — so the field could show text the state did not agree with
 * and never be corrected:
 *
 *   type 1 after a leading 0   "010000" parses to 10000, which is already
 *                              the state, so React left "010000" on screen
 *   backspace the last digit   "0" parses to 0, which is already the state,
 *                              so the 0 could never be deleted
 *
 * Holding the text means what is typed is what is shown. The number is
 * derived from it for the caller, and the text is re-synced from the prop
 * only when the field is NOT focused — so a preset button lands in the box
 * while typing is never overwritten mid-keystroke.
 *
 * `inputMode="numeric"` rather than `type="number"`: a number input adds
 * spinners, accepts `e` and `-`, and makes the caret behave differently on
 * every browser. A text field with a digits-only filter is the boring one
 * that behaves the same everywhere.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * A whole-number box you can empty while you think.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CONTROLLED NUMBER INPUT CANNOT BE CLEARED, AND THAT IS THE WHOLE BUG
 *
 * The lots stepper was `value={lots}` with `onChange` doing
 * `Math.max(1, Number(...) || 1)`. Backspace made the box empty, `Number('')`
 * is 0, `|| 1` made it 1, and the 1 was written straight back — so the field
 * snapped to 1 on the first keystroke and there was no way to type 300.
 * Reported 2026-10-29.
 *
 * The fix is to let the INPUT hold a string while it is being typed into and
 * only tell the parent about numbers. An empty box is a valid thing to be
 * looking at; it is not a valid thing to trade on, so the parent simply is
 * not told until there is a digit.
 *
 * DIGITS ONLY, enforced by stripping rather than by `type="number"`. A number
 * input still accepts `e`, `+`, `-` and `.` in most browsers, reports them as
 * an empty value, and silently loses what was typed. Stripping is the only
 * version where what you see is what the field holds.
 *
 * `onBlur` re-reads from the parent, so "007" settles to "7" and a box left
 * empty comes back to the value that is actually in force.
 * ─────────────────────────────────────────────────────────────────────────────
 */
